import BASE_URL from "../../config/api";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import ImageSkeleton from "./ImageSkeleton";
import { BsChevronLeft, BsChevronRight, BsX } from "react-icons/bs";


const Picture = () => {
  const [loading, setLoading] = useState(false);
  const [pictures, setPictures] = useState([]);
  const [project, setProject] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { id } = useParams();

  const runOnStart = async () => {
    setLoading(true);
    await axios
      .get(`${BASE_URL}/construct/get-construction/${id}`)
      .then((response) => {
        const data = response.data.data;
        setPictures(data.pictures || []);
        setProject(data);
      })
      .catch(() => {});
    setLoading(false);
  };

  useEffect(() => { runOnStart(); }, [id]);

  const prev = useCallback(() => {
    setLightboxIndex((i) => (i > 0 ? i - 1 : pictures.length - 1));
  }, [pictures.length]);

  const next = useCallback(() => {
    setLightboxIndex((i) => (i < pictures.length - 1 ? i + 1 : 0));
  }, [pictures.length]);

  const close = useCallback(() => setLightboxIndex(null), []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, prev, next, close]);

  const currentPic = lightboxIndex !== null ? pictures[lightboxIndex] : null;

  return (
    <div className="font-Montserrat min-h-screen bg-gray-50">
      <div className="h-[56px] md:h-[72px]" />

      {/* Page header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-10 py-8 md:py-12">
          <Link to="/construction" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#1877F2] transition-colors mb-4">
            ← Back to Portfolio
          </Link>
          <div className="flex items-start gap-3 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{project?.title || "Project Gallery"}</h1>
            {project?.status && (
              <span className={`mt-1 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide ${
                project.status === "Completed" ? "bg-green-100 text-green-700" : "bg-[#1877F2]/10 text-[#1877F2]"
              }`}>
                {project.status}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
            {project?.location && <span>📍 {project.location}</span>}
            {project?.year && <span>📅 {project.year}</span>}
            {project?.clientName && <span>👤 {project.clientName}</span>}
            <span>{pictures.length} photo{pictures.length !== 1 ? "s" : ""}{project?.videos?.length ? ` · ${project.videos.length} video${project.videos.length !== 1 ? "s" : ""}` : ""}</span>
          </div>
          {project?.description && (
            <p className="text-gray-500 text-sm mt-3 max-w-2xl">{project.description}</p>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-10 py-10">
        {loading ? (
          <ImageSkeleton />
        ) : pictures.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {pictures.map((pic, index) => (
              <button
                key={index}
                onClick={() => setLightboxIndex(index)}
                className="group relative rounded-xl overflow-hidden bg-gray-200 aspect-square focus:outline-none focus:ring-2 focus:ring-[#1877F2]"
              >
                <img
                  src={pic.picture}
                  alt={pic.details || `Photo ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end">
                  {pic.details && (
                    <p className="text-white text-xs font-medium px-3 py-2 w-full translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      {pic.details}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <p className="text-4xl mb-4">📷</p>
            <p className="text-gray-400 text-lg font-medium">No photos uploaded yet.</p>
          </div>
        )}
      </div>

      {/* Videos */}
      {project?.videos?.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 md:px-10 pb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-5">Project Videos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {project.videos.map((url, i) => (
              <div key={i} className="rounded-2xl overflow-hidden shadow-sm aspect-video bg-gray-900">
                <video src={url} controls className="w-full h-full" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {currentPic && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={close}
        >
          {/* Close */}
          <button
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            onClick={close}
          >
            <BsX size={22} />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm font-medium">
            {lightboxIndex + 1} / {pictures.length}
          </div>

          {/* Prev */}
          <button
            className="absolute left-3 md:left-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-[#1877F2] flex items-center justify-center text-white transition-colors disabled:opacity-30"
            onClick={(e) => { e.stopPropagation(); prev(); }}
          >
            <BsChevronLeft size={18} />
          </button>

          {/* Image */}
          <div
            className="max-w-4xl w-full px-16 md:px-24"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentPic.picture}
              alt={currentPic.details}
              className="w-full max-h-[80vh] object-contain rounded-xl select-none"
              draggable={false}
            />
            {currentPic.details && (
              <p className="text-white/60 text-sm text-center mt-3">{currentPic.details}</p>
            )}
          </div>

          {/* Next */}
          <button
            className="absolute right-3 md:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-[#1877F2] flex items-center justify-center text-white transition-colors disabled:opacity-30"
            onClick={(e) => { e.stopPropagation(); next(); }}
          >
            <BsChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Picture;
